import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api, { enhancedApi, mapApiErrorsToForm } from '../api';
import { validators, runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { useDuplicateCheck } from '../hooks/useDuplicateCheck';
import FormHelperText from '@mui/material/FormHelperText';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const invoiceFormSchema = {
  client: [validators.required],
  amount: [validators.required, validators.positiveNumber, validators.maxValue(10000000)],
  due_date: [],
};

const invoiceAvailableHelper = (
  <Typography component="span" variant="caption" sx={{ color: 'success.main' }}>
    Available
  </Typography>
);

const CreateInvoice = ({ onBack, onSaveInvoice, clientId, invoiceId }) => {
  const {
    checking: invDupChecking,
    results: invDupResults,
    checkInvoiceNumber,
    clearField: clearInvDupField,
  } = useDuplicateCheck();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientCredits, setClientCredits] = useState([]);
  const [applyCredits, setApplyCredits] = useState(false);
  const [creditsToApply, setCreditsToApply] = useState(0);
  const [invoiceDetails, setInvoiceDetails] = useState({
    clientName: "",
    description: "",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().split('T')[0],
    terms: "Due on Receipt",
    dueDate: "",
  });

  const [openSettings, setOpenSettings] = useState(false);
  const [settings, setSettings] = useState({
    columnsVisible: 5,
    lineItems: "Grouped",
    clientNote: false,
    clientNoteText: "",
    termsAndConditions: true,
    paymentInstructions: `To avoid paying the CC/Debit fee, follow these steps:\n1. Either click on VIEW INVOICE or Pay Now.\n2. Click on Bank account versus the...`,
  });

  // State for PDF preview
  const [openPreview, setOpenPreview] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  // Initialize default layout plugin for PDF viewer
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Fetch client name based on clientId
  useEffect(() => {
    const fetchClientName = async () => {
      if (!clientId) return;
      try {
        const response = await api.get(`/api/multi-tenant/client-name/?clientId=${clientId}`);
        const fetchedClientName = response.data.clientName || 'Unknown Client';
        setInvoiceDetails((prev) => ({
          ...prev,
          clientName: fetchedClientName,
        }));
        // Optionally add the fetched client name to the clients list
        setClients([{ name: fetchedClientName }]);
      } catch {
        setInvoiceDetails((prev) => ({
          ...prev,
          clientName: 'Unknown Client',
        }));
      }
    };
    fetchClientName();
  }, [clientId]);

  // Fetch client credits when clientId or selectedClientId is available
  const effectiveClientId = clientId || selectedClientId;

  useEffect(() => {
    const fetchCredits = async () => {
      if (!effectiveClientId) return;
      try {
        const response = await api.get(`/api/multi-tenant/clients/${effectiveClientId}/credits/`);
        const credits = Array.isArray(response.data) ? response.data : (response.data?.credits || []);
        const openCredits = credits.filter(c => (c.status || c.credit_status) === 'Open' || !c.status);
        setClientCredits(openCredits);
      } catch {
        setClientCredits([]);
      }
    };
    fetchCredits();
  }, [effectiveClientId]);

  // Fetch clients list when no clientId (firm-level create)
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get("/api/multi-tenant/clients/");
        const data = Array.isArray(response.data) ? response.data : (response.data?.results || response.data?.clients || []);
        const clientList = data.map(client => ({
          id: client.id,
          name: client.display_name || client.primary_contact?.display_name || client.primary_contact?.first_name || "Unknown",
        }));
        setClients(clientList);
      } catch {
        setClients([]);
      }
    };
    if (!clientId) {
      fetchClients();
      setSelectedClientId(null);
    }
  }, [clientId]);

  useEffect(() => {
    const generateInvoiceNumber = () => {
      const randomNumber = Math.floor(10000 + Math.random() * 90000);
      setInvoiceDetails((prevDetails) => ({
        ...prevDetails,
        invoiceNumber: randomNumber.toString(),
      }));
    };
    generateInvoiceNumber();
  }, []);

  const calculateDueDate = (invoiceDate, terms) => {
    const date = new Date(invoiceDate);
    if (terms === 'Net 15') date.setDate(date.getDate() + 15);
    else if (terms === 'Net 30') date.setDate(date.getDate() + 30);
    else if (terms === 'Net 45') date.setDate(date.getDate() + 45);
    else if (terms === 'Net 60') date.setDate(date.getDate() + 60);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setInvoiceDetails((prev) => ({
      ...prev,
      invoiceDate: today,
      dueDate: today,
    }));
  }, []);

  const [items, setItems] = useState([
    {
      service: "",
      description: "",
      quantity: 1,
      rateType: "Other",
      rate: 0,
      amount: 0,
      discount: 0,
      tax: 0,
      lineTotal: 0,
    },
  ]);

  const {
    errors: invErrors,
    touched: invTouched,
    validateField: invValidateField,
    touchField: invTouchField,
    validateAll: invValidateAll,
    clearErrors: invClearErrors,
    mergeServerFieldErrors: invMergeServerErrors,
  } = useFormValidation(invoiceFormSchema);

  const [lineItemErrors, setLineItemErrors] = useState({});
  const [linesTouched, setLinesTouched] = useState(false);

  const invoiceTotalStr = useMemo(
    () => items.reduce((total, item) => total + (item.lineTotal || 0), 0).toFixed(2),
    [items]
  );

  const invoiceFormValues = useMemo(
    () => ({
      client: String(effectiveClientId ?? ''),
      amount: invoiceTotalStr,
      due_date: invoiceDetails.dueDate || '',
    }),
    [effectiveClientId, invoiceTotalStr, invoiceDetails.dueDate]
  );

  const invoiceMetaValid = useMemo(
    () =>
      !Object.keys(invoiceFormSchema).some((k) =>
        runValidators(invoiceFormValues[k], invoiceFormSchema[k])
      ),
    [invoiceFormValues]
  );

  const linesValid = useMemo(
    () =>
      items.length > 0 && items.every((it) => String(it.description || '').trim().length > 0),
    [items]
  );

  const validateLineItems = useCallback(() => {
    const err = {};
    if (!items.length) err.__root = 'Add at least one line item.';
    items.forEach((it, i) => {
      if (!String(it.description || '').trim()) err[i] = 'Description is required';
    });
    setLineItemErrors(err);
    setLinesTouched(true);
    return Object.keys(err).length === 0;
  }, [items]);

  const [qbItems, setQbItems] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);

  useEffect(() => {
    const fetchQbItems = async () => {
      try {
        const res = await enhancedApi.getQuickBooksItems();
        if (res.data?.items?.length) {
          setQbItems(res.data.items);
        }
      } catch {
        // QuickBooks not connected or failed - leave qbItems empty
      }
    };
    fetchQbItems();
  }, []);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await api.get('/api/multi-tenant/tax-service-catalog/');
        if (res.data?.services?.length) {
          setCatalogServices(res.data.services);
        }
      } catch {
        setCatalogServices([]);
      }
    };
    fetchCatalog();
  }, []);

  const handleServiceSelect = (index, value) => {
    const catalogItem = catalogServices.find((s) => s.name === value);
    const qbItem = qbItems.find((q) => q.name === value);
    const source = catalogItem || qbItem;
    setItems((prev) => {
      const updated = [...prev];
      const rate = catalogItem?.default_rate ?? qbItem?.unit_price ?? updated[index].rate ?? 0;
      const desc = source?.description ?? updated[index].description ?? '';
      updated[index] = {
        ...updated[index],
        service: value,
        description: desc,
        rate,
        document_type: catalogItem?.document_type ?? updated[index].document_type ?? '',
        amount: (updated[index].quantity || 1) * rate,
      };
      updated[index].amount = updated[index].quantity * updated[index].rate;
      updated[index].lineTotal =
        (updated[index].quantity * updated[index].rate -
          (updated[index].quantity * updated[index].rate * (updated[index].discount || 0) / 100)) *
        (1 + (updated[index].tax || 0) / 100);
      return updated;
    });
  };

  const catalogServiceNames = catalogServices.map((s) => s.name);
  const qbServiceNames = qbItems.map((q) => q.name);
  const allServiceOptions = [...new Set([...catalogServiceNames, ...qbServiceNames].filter(Boolean))];

  // Existing functions (unchanged)
  const updateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] =
      field === 'quantity' || field === 'rate' || field === 'discount' || field === 'tax'
        ? parseFloat(value) || 0
        : value;
    updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    updatedItems[index].lineTotal =
      (updatedItems[index].quantity * updatedItems[index].rate -
        (updatedItems[index].quantity * updatedItems[index].rate *
          (updatedItems[index].discount || 0) / 100)) *
      (1 + (updatedItems[index].tax || 0) / 100);
    setItems(updatedItems);
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDetails({ ...invoiceDetails, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] =
      field === 'quantity' || field === 'rate' ? parseFloat(value) || 0 : value;
    updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        service: "",
        description: "",
        quantity: 1,
        rateType: "Other",
        rate: 0,
        amount: 0,
        discount: 0,
        tax: 0,
        lineTotal: 0,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const calculateTotal = () => {
    return items.reduce((total, item) => total + item.lineTotal, 0).toFixed(2);
  };

  const totalCreditsAvailable = clientCredits.reduce((sum, c) => sum + (parseFloat(c.credit_remaining) || 0), 0);
  const invoiceTotal = parseFloat(calculateTotal()) || 0;
  const maxCreditsToApply = Math.min(totalCreditsAvailable, invoiceTotal);

  const saveInvoiceToBackend = async (invoiceData) => {
    try {
      await api.post('/api/multi-tenant/create-single-invoice/', invoiceData);
      invClearErrors();
      setLineItemErrors({});
      setLinesTouched(false);
      alert('Invoice saved successfully!');
    } catch (error) {
      const mapped = mapApiErrorsToForm(error.response?.data);
      if (Object.keys(mapped).length) invMergeServerErrors(mapped);
      alert(error.response?.data?.detail || error.response?.data?.error || 'Failed to save invoice. Please try again.');
    }
  };

  const handleSaveInvoice = () => {
    if (!invValidateAll(invoiceFormValues) || !validateLineItems()) return;
    const invoiceData = {
      ...invoiceDetails,
      items,
      totalAmount: calculateTotal(),
      clientNote: settings.clientNote ? settings.clientNoteText : "",
      clientId: effectiveClientId || null,
      creditsToApply: applyCredits ? Math.min(parseFloat(creditsToApply) || 0, maxCreditsToApply) : 0,
    };
    if (onSaveInvoice) {
      onSaveInvoice(invoiceData);
    }
    saveInvoiceToBackend(invoiceData);
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceId) {
      onBack?.();
      return;
    }
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;
    try {
      await enhancedApi.deleteInvoice(invoiceId);
      onBack?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete invoice.');
    }
  };

  const handleSettings = () => {
    setOpenSettings(true);
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
  };

  const handleSaveSettings = () => {
    setOpenSettings(false);
  };

  const handleSettingsChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTermsChange = (event) => {
    const newTerms = event.target.value;
    setInvoiceDetails((prev) => ({
      ...prev,
      terms: newTerms,
      dueDate: calculateDueDate(prev.invoiceDate, newTerms),
    }));
  };

  // Handle PDF preview
  const handlePreviewInvoice = async () => {
    if (!invValidateAll(invoiceFormValues) || !validateLineItems()) return;
    try {
      const creditsForPreview = applyCredits ? Math.min(parseFloat(creditsToApply) || 0, maxCreditsToApply) : 0;
      const invoiceData = {
        ...invoiceDetails,
        items,
        totalAmount: calculateTotal(),
        clientNote: settings.clientNote ? settings.clientNoteText : "",
        street1: "N/A",
        street2: "",
        zipcode: "N/A",
        state: "N/A",
        country: "N/A",
        paymentInstructions: settings.paymentInstructions,
        creditsToApply: creditsForPreview,
      };

      const response = await api.post('/api/multi-tenant/preview-invoice/', invoiceData);
      setPdfData(`data:application/pdf;base64,${response.data.pdf}`);
      setOpenPreview(true);
    } catch {
      alert('Failed to generate PDF preview. Please try again.');
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setPdfData(null);
  };

  return (
    <Box>
    <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', maxWidth: 1400, mx: 'auto' }}>
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} sx={{ mr: 1, bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
            Create Invoice
          </Typography>
          <IconButton onClick={handleSettings} sx={{ ml: 1, bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }} aria-label="Invoice settings">
            <SettingsIcon />
          </IconButton>
        </Box>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          {invoiceId && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteInvoice} sx={{ borderRadius: 2 }}>
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleSaveInvoice}
            disabled={!invoiceMetaValid || !linesValid}
            sx={{ borderRadius: 2 }}
          >
            Save as draft
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 600 }}
            onClick={handlePreviewInvoice}
            disabled={!invoiceMetaValid || !linesValid}
          >
            Preview
          </Button>
        </Box>
      </Box>

      {/* Main Content: Responsive Two-Column Layout */}
      <Grid container spacing={4}>
        {/* Left/Main Column */}
        <Grid item xs={12} md={12}>
          {/* Invoice Details Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={Boolean(invTouched.client && invErrors.client)}
                  disabled={!!clientId}
                >
                  <InputLabel>Client Name</InputLabel>
                  <Select
                    name="clientName"
                    value={invoiceDetails.clientName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const sel = clients.find((c) => c.name === val || c.id === val);
                      if (sel) {
                        setSelectedClientId(sel.id);
                        setInvoiceDetails((prev) => ({ ...prev, clientName: sel.name }));
                        if (invTouched.client) invValidateField('client', String(sel.id));
                      } else {
                        handleInvoiceChange(e);
                        if (invTouched.client) invValidateField('client', '');
                      }
                    }}
                    onBlur={() => invTouchField('client', String(effectiveClientId ?? ''))}
                  >
                    {clientId ? (
                      invoiceDetails.clientName ? (
                        <MenuItem value={invoiceDetails.clientName}>{invoiceDetails.clientName}</MenuItem>
                      ) : (
                        <MenuItem disabled>Loading...</MenuItem>
                      )
                    ) : (
                      <>
                        <MenuItem value="">Select a client</MenuItem>
                        {clients.map((c) => (
                          <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                        ))}
                      </>
                    )}
                  </Select>
                  {(invTouched.client && invErrors.client) ? (
                    <FormHelperText error>{invErrors.client}</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Description"
                  variant="outlined"
                  fullWidth
                  name="description"
                  value={invoiceDetails.description}
                  onChange={handleInvoiceChange}
                  placeholder="Add description (limit 65 characters)"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Invoice #"
                  variant="outlined"
                  fullWidth
                  name="invoiceNumber"
                  value={invoiceDetails.invoiceNumber}
                  onChange={handleInvoiceChange}
                  onBlur={(e) => {
                    if (!invoiceId) {
                      checkInvoiceNumber(e.target.value, {
                        duplicateMessage: 'This invoice number is already in use',
                      });
                    }
                  }}
                  error={!invoiceId && typeof invDupResults.invoiceNumber === 'string'}
                  helperText={
                    !invoiceId &&
                    (typeof invDupResults.invoiceNumber === 'string'
                      ? invDupResults.invoiceNumber
                      : invDupResults.invoiceNumber === 'ok'
                        ? invoiceAvailableHelper
                        : undefined)
                  }
                  InputProps={{
                    endAdornment:
                      invoiceId ||
                      (!invDupChecking.invoiceNumber &&
                        typeof invDupResults.invoiceNumber !== 'string' &&
                        invDupResults.invoiceNumber !== 'ok')
                        ? undefined
                        : (
                            <InputAdornment position="end" sx={{ gap: 0.5 }}>
                              {invDupChecking.invoiceNumber ? (
                                <CircularProgress size={16} />
                              ) : typeof invDupResults.invoiceNumber === 'string' ? (
                                <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                              ) : invDupResults.invoiceNumber === 'ok' ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : null}
                            </InputAdornment>
                          ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Invoice Date"
                  variant="outlined"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  name="invoiceDate"
                  value={invoiceDetails.invoiceDate}
                  onChange={handleInvoiceChange}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Terms</InputLabel>
                  <Select
                    name="terms"
                    value={invoiceDetails.terms}
                    onChange={handleTermsChange}
                    fullWidth
                  >
                    <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                    <MenuItem value="Net 15">Net 15</MenuItem>
                    <MenuItem value="Net 30">Net 30</MenuItem>
                    <MenuItem value="Net 45">Net 45</MenuItem>
                    <MenuItem value="Net 60">Net 60</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Due Date"
                  variant="outlined"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  name="dueDate"
                  value={invoiceDetails.dueDate}
                  onChange={(e) => {
                    handleInvoiceChange(e);
                    if (invTouched.due_date) invValidateField('due_date', e.target.value);
                  }}
                  onBlur={() => invTouchField('due_date', invoiceDetails.dueDate || '')}
                  error={Boolean(invTouched.due_date && invErrors.due_date)}
                  helperText={invTouched.due_date && invErrors.due_date ? invErrors.due_date : undefined}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Line Items Table */}
          <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              Line Items
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', maxHeight: 480 }}>
            <Table sx={{ minWidth: 800, tableLayout: 'auto' }} stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Tax</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Select
                        fullWidth
                        size="small"
                        value={item.service}
                        onChange={(e) => handleServiceSelect(index, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select a Service</MenuItem>
                        {qbItems.length > 0 && (
                          <MenuItem value="" disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                            — QuickBooks —
                          </MenuItem>
                        )}
                        {qbItems.map((q) => (
                          <MenuItem key={q.id} value={q.name}>
                            {q.name}{q.unit_price ? ` ($${q.unit_price})` : ''}
                          </MenuItem>
                        ))}
                        {qbItems.length > 0 && (
                          <MenuItem value="" disabled sx={{ fontWeight: 600, color: 'primary.main' }}>
                            — Default —
                          </MenuItem>
                        )}
                        {catalogServiceNames.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={item.description}
                        error={Boolean(linesTouched && lineItemErrors[index])}
                        helperText={linesTouched ? lineItemErrors[index] : undefined}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleItemChange(index, 'description', v);
                          if (linesTouched) {
                            setLineItemErrors((prev) => {
                              const next = { ...prev };
                              if (String(v || '').trim()) delete next[index];
                              else next[index] = 'Description is required';
                              delete next.__root;
                              return next;
                            });
                          }
                        }}
                        onBlur={() => {
                          setLinesTouched(true);
                          if (!String(item.description || '').trim()) {
                            setLineItemErrors((prev) => ({ ...prev, [index]: 'Description is required' }));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", e.target.value)}
                        inputProps={{ min: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, "rate", e.target.value)}
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>${item.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.discount}
                        onChange={(e) => updateItem(index, "discount", e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.tax}
                        onChange={(e) => updateItem(index, "tax", e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        type="number"
                        value={item.lineTotal}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleRemoveItem(index)} color="secondary">
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
            {linesTouched && lineItemErrors.__root ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {lineItemErrors.__root}
              </Typography>
            ) : null}
            <Button variant="outlined" onClick={handleAddItem} sx={{ mt: 2, borderRadius: 2 }}>
              + Add line item
            </Button>
          </Paper>

          {/* Credits Available - when client has credits */}
          {effectiveClientId && totalCreditsAvailable > 0 && (
            <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', borderColor: 'success.main', borderWidth: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.dark' }}>
                Credits Available
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Total credits available for this client: ${totalCreditsAvailable.toFixed(2)}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={applyCredits}
                    onChange={(e) => {
                      setApplyCredits(e.target.checked);
                      if (!e.target.checked) setCreditsToApply(0);
                      else setCreditsToApply(Math.min(totalCreditsAvailable, parseFloat(calculateTotal()) || 0));
                    }}
                  />
                }
                label="Apply credits to this invoice"
              />
              {applyCredits && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Amount to apply"
                    type="number"
                    value={creditsToApply}
                    onChange={(e) => setCreditsToApply(Math.min(parseFloat(e.target.value) || 0, maxCreditsToApply))}
                    inputProps={{ min: 0, max: maxCreditsToApply, step: 0.01 }}
                    sx={{ width: 160 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    (Max: ${maxCreditsToApply.toFixed(2)})
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Summary Card - now full width and below main content */}
          <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Summary</Typography>
            {invTouched.amount && invErrors.amount ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {invErrors.amount}
              </Typography>
            ) : null}
            <Box
              onBlur={() => invTouchField('amount', invoiceTotalStr)}
              sx={{ outline: 'none' }}
            >
              <Typography variant="body1">Subtotal: ${calculateTotal()}</Typography>
              <Typography variant="body1">Invoice Total: ${calculateTotal()}</Typography>
              {applyCredits && creditsToApply > 0 && (
                <Typography variant="body1" color="success.main">
                  Credits Applied: -${creditsToApply.toFixed(2)}
                </Typography>
              )}
              <Typography variant="body1" fontWeight="bold">
                Balance Due: ${(parseFloat(calculateTotal()) - (applyCredits ? creditsToApply : 0)).toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Settings Dialog */}
      <Dialog open={openSettings} onClose={handleCloseSettings}>
        <DialogTitle>Layout and Settings</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="subtitle1">Layout</Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Columns</InputLabel>
              <Select
                value={settings.columnsVisible}
                onChange={(e) => handleSettingsChange("columnsVisible", e.target.value)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <MenuItem key={num} value={num}>{num} columns visible</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box mt={2}>
              <Typography variant="body2">Line items</Typography>
              <RadioGroup
                row
                value={settings.lineItems}
                onChange={(e) => handleSettingsChange("lineItems", e.target.value)}
              >
                <FormControlLabel value="Separate" control={<Radio />} label="Separate" />
                <FormControlLabel value="Grouped" control={<Radio />} label="Grouped" />
              </RadioGroup>
            </Box>
          </Box>
          <Box mb={2}>
            <Typography variant="subtitle1">Settings</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.clientNote}
                  onChange={(e) => handleSettingsChange("clientNote", e.target.checked)}
                />
              }
              label="Client Note"
            />
            {settings.clientNote && (
              <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={4}>
                <Typography variant="body2" gutterBottom>
                  Client Note
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={settings.clientNoteText}
                  onChange={(e) => handleSettingsChange("clientNoteText", e.target.value)}
                  variant="outlined"
                  placeholder="Enter client note here..."
                />
              </Box>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.termsAndConditions}
                  onChange={(e) => handleSettingsChange("termsAndConditions", e.target.checked)}
                />
              }
              label="Terms and Conditions"
            />
          </Box>
          <Box p={2} border={1} borderColor="grey.300" borderRadius={4}>
            <Typography variant="body2" gutterBottom>
              Payment Instructions
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={settings.paymentInstructions}
              onChange={(e) => handleSettingsChange("paymentInstructions", e.target.value)}
              variant="outlined"
              placeholder="Enter payment instructions here..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={openPreview} onClose={handleClosePreview} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Invoice Preview</DialogTitle>
        <DialogContent sx={{ minHeight: '70vh', p: 0, '& > div': { height: '100%' } }}>
          {pdfData ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfData}
                plugins={[defaultLayoutPluginInstance]}
              />
            </Worker>
          ) : (
            <Typography>Loading PDF...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </Box>
  );
};

export default CreateInvoice;